import React from "react";
import { Box, Button, Chip, IconButton, Tooltip, Typography } from "@material-ui/core";
import { ArrowBack } from "@material-ui/icons";
import { css } from "@emotion/css";
import { useHistory, useParams } from "react-router-dom";
import { ErrorBox, Loading } from "shared";
import { Colors } from "theme";
import TextDiff from "routes/healthForms/components/TextDiff";
import {
  CatalogueEntry,
  CatalogueKind,
  isEnvGroup,
  useDeployCatalogueEntry,
  useGetDeployableCatalogueEntry,
  useGetDeployedCatalogueEntry,
  useRevertCatalogueEntryToDeployed,
} from "routes/createContent/api/catalogueDeployments";
import { useKeepView } from "utils/useViewState";
import { serializeCatalogueEntry } from "./serializeCatalogueEntry";
import { CatalogueGaps } from "./CatalogueGaps";

export const CatalogueChangesPage: React.VFC<{ kind: CatalogueKind; backTo: string }> = ({ kind, backTo }) => {
  const history = useHistory();
  const { name, group } = useParams<{ name: string; group: string }>();
  const keep = useKeepView([]);
  const envGroup = isEnvGroup(group) ? group : null;

  const deployedQuery = useGetDeployedCatalogueEntry(kind, name, envGroup ?? "prod", envGroup !== null);
  const deployableQuery = useGetDeployableCatalogueEntry(kind, name, envGroup !== null);
  const deploy = useDeployCatalogueEntry();
  const revert = useRevertCatalogueEntryToDeployed();
  const deployable = envGroup !== null && deployableQuery.data?.rollout?.[envGroup] !== undefined;

  const deployed = deployedQuery.data ?? null;
  const current = deployableQuery.data ?? null;
  const busy = deploy.isLoading || revert.isLoading;
  const back = () => history.push(keep(backTo));

  if (!envGroup) {
    return (
      <Typography variant="body2" color="textSecondary" className={styles.message}>
        Unknown environment group &quot;{group}&quot;.
      </Typography>
    );
  }

  if (deployedQuery.isLoading || deployableQuery.isLoading) {
    return <Loading />;
  }

  if (deployedQuery.isError || deployableQuery.isError) {
    return <ErrorBox error={deployedQuery.error ?? deployableQuery.error} verbose className={styles.message} />;
  }

  const previous = deployed ? serializeCatalogueEntry(deployed) : "";
  const next = current ? serializeCatalogueEntry(current) : "";
  const changed = previous !== next;
  const deleting = entryDeleted(current) && !entryDeleted(deployed);
  const gaps = envGroup === "prod" && !deleting ? (current?.gaps ?? []) : [];

  return (
    <Box className={styles.page}>
      <Box className={styles.header}>
        <Tooltip title={`Back to ${kind}s`}>
          <IconButton size="small" onClick={back} aria-label="Back">
            <ArrowBack />
          </IconButton>
        </Tooltip>
        <Typography variant="h5">{name}</Typography>
        <Chip
          size="small"
          color={changed ? "primary" : "default"}
          label={deleting ? `Deleted, still on ${envGroup}` : changed ? `Not on ${envGroup}` : `Matches ${envGroup}`}
        />
        <Box flexGrow={1} />
        {changed && (
          <>
            <Button
              className={styles.discard}
              disabled={!deployed || busy}
              onClick={() => revert.mutate({ kind, name, group: envGroup }, { onSuccess: back })}
            >
              {deleting ? "Restore" : "Discard saved changes"}
            </Button>
            {deployable && (
              <Button
                color="primary"
                variant="contained"
                disabled={busy || gaps.length > 0}
                onClick={() => deploy.mutate({ kind, name, group: envGroup }, { onSuccess: back })}
              >
                {deleting ? `Deploy deletion to ${envGroup}` : `Deploy to ${envGroup}`}
              </Button>
            )}
          </>
        )}
      </Box>

      <CatalogueGaps gaps={gaps} />
      {deploy.isError && <ErrorBox error={deploy.error} verbose />}

      {deleting && (
        <Typography variant="body2" color="textSecondary">
          This {kind} is deleted. {envGroup} keeps serving it until the deletion is deployed.
        </Typography>
      )}

      <Box className={styles.body}>
        {!deployed ? (
          <Typography variant="body2" color="textSecondary" className={styles.message}>
            Nothing is deployed to {envGroup} yet, so there is nothing to compare against.
          </Typography>
        ) : (
          <TextDiff previous={previous} next={next} previousLabel={`On ${envGroup}`} nextLabel="Saved" />
        )}
      </Box>
    </Box>
  );
};

const entryDeleted = (entry: CatalogueEntry | null) =>
  Boolean(entry?.exercise?.retired || entry?.lesson?.retired || entry?.template?.retired);

const styles = {
  page: css`
    display: flex;
    flex-direction: column;
    height: calc(100vh - 154px);
    box-sizing: border-box;
    gap: 16px;
  `,
  header: css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
  `,
  body: css`
    flex: 1;
    overflow: auto;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    background: #fff;
  `,
  message: css`
    padding: 16px;
  `,
  discard: css`
    && {
      color: ${Colors.ERROR};
    }
    &&.Mui-disabled {
      color: rgba(0, 0, 0, 0.26);
    }
  `,
};
